import { Injectable } from '@angular/core';
import { Message } from '@app/ngrx-message/models/client/message';
import { MessageStoreService } from '@app/ngrx-message/services/message/message-store.service';
import {
  CompleteInventoryConflictErrorMessage,
  InventoryToastMessages,
} from '@inventory/constants/inventory-constants';
import { InventoryService } from '@inventory/services/inventory.service';
import { ToastController, ToastOptions } from '@ionic/angular';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import {
  createInventoryItemLookup,
  generatePendingItemUpdateKey,
  INVENTORY_ACTIONS,
  INVENTORY_GROUP_ACTIONS,
  INVENTORY_ITEM_ACTIONS,
  INVENTORY_WORKSHEET_MODE_DEFAULT,
  InventoryItemLookup,
  InventoryItemUpdateV2,
  PendingItemUpdateKey,
  ToastParams,
} from '@usf/ngrx-inventory';
import { InventoryState } from '@usf/ngrx-inventory/lib/models/inventory/inventory.model';
import {
  ExternalVendorActions,
  NonUSFProductActions,
  ProductStateService,
  selectProductSummaryEntities,
  allNonUSFProducts,
} from '@usf/ngrx-product';
import { combineLatest, Observable, of } from 'rxjs';
import {
  concatMap,
  filter,
  map,
  mergeMap,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { CustomerStoreService } from '../../ngrx-customer/services';
import { MessageTypeEnum } from '../../ngrx-message/constants/messageTypeEnum';
import { DownloadInventoryService } from '../../shared/services/document/download-inventory/download-inventory.service';
import { ProductService } from '../../shared/services/product/product.service';
import { ToastService } from '../../shared/services/toast/toast.service';
import { InventoryWorksheetService } from '../pages/inventory-worksheet/services/inventory-worksheet.service';
import { convertStringToDate } from '@shared/helpers/calendar.helpers';
import { PATHS } from '@shared/constants/paths';
import { NonUsfProductViewModelSelectors } from '@inventory/pages/manage-non-usf-products/selectors/non-usf-selectors';
import { Store } from '@ngrx/store';
import { getProductCombinationMapForReports } from '@inventory/selectors/helpers/inventory-view-model-helper';
import { DownloadProductsService } from '@shared/services/document/download-products/download-products.service';
import { InventoryCreateSelectors } from '@inventory/selectors/inventory-create.selector';
import { productsAreInStateWithPricing } from '@shared/selectors/helpers/product-info.selectors.helper';
import { updateInventoryItemWithProductInformation } from '@inventory/selectors/helpers/inventory-create-update-helper';
import { Product } from '@shared/models/product.model';
import {
  InventoryGroupItem,
  InventoryUpdatedByUser,
} from '@usf/inventory-types';
import { NonUSFProduct } from '@usf/product-types';

@Injectable({
  providedIn: 'root',
})
export class InventoryEffects {
  priceSystemChange$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(INVENTORY_ACTIONS.updateSelectedPrice),
        tap(action => {
          this.inventoryService
            .selectInventoryById$(action.inventoryId)
            .pipe(take(1))
            .subscribe(inventory => {
              const numbersFromItems = [];
              inventory.groups.forEach(group => {
                group.groupItems.forEach(item => {
                  if (!numbersFromItems.includes(item.itemNumber)) {
                    numbersFromItems.push(item.itemNumber);
                  }
                });
              });
              this.inventoryService.updateInventoryBasedOnProductNumbersAndInventory(
                numbersFromItems,
                inventory,
                action.selectedPrice,
              );
            });
        }),
      ),
    { dispatch: false },
  );

  priceOverrideCheck$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(INVENTORY_ITEM_ACTIONS.updatePriceOverride),
        tap(action => {
          if (
            action.overrideValue === undefined ||
            action.overrideValue === 0
          ) {
            this.inventoryService
              .selectInventoryById$(action.inventoryId)
              .pipe(take(1))
              .subscribe(inventory => {
                let group = inventory.groups.find(
                  value => value.groupName === action.groupName,
                );
                if (group) {
                  let item = group.groupItems.find(
                    value => value.itemNumber === action.inventoryItemId,
                  );
                  if (item) {
                    this.inventoryService
                      .selectInventoryOptionPrice$()
                      .pipe(take(1))
                      .subscribe(pricingValue => {
                        this.inventoryService.updateInventoryItemBasedOnProductNumber(
                          action.inventoryId,
                          action.groupName,
                          item,
                          pricingValue,
                        );
                      });
                  }
                }
              });
          }
        }),
      ),
    { dispatch: false },
  );

  createInventorySuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(INVENTORY_ACTIONS.createSuccess),
        tap(action => {
          this.inventoryService.goToWorksheet(action.inventory.inventoryId);
          const message = action.copiedInvName
            ? this.translateService.instant(
                'i18n.inventory.createCopiedInventorySuccess',
              ) +
              ' ' +
              action.copiedInvName +
              this.translateService.instant(
                'i18n.inventory.createCopiedInventorySuccessTitle',
              )
            : 'i18n.inventory.createInventorySuccess';
          this.displaySuccessToast(message, action.inventory.inventoryId);
          if (
            action.productsNotFound !== undefined &&
            action.productsNotFound > 0
          ) {
            // make sure the toast timing presents after success completes
            setTimeout(() => {
              this.displayCreateWithMissingProductsToast(
                action.productsNotFound,
              );
            }, 5000);
          }
        }),
      ),
    { dispatch: false },
  );

  moveItemDuplicateFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(INVENTORY_ITEM_ACTIONS.moveInventoryItemDuplicateFailure),
        tap(() => {
          const translation =
            InventoryToastMessages['moveItemDuplicateFailure'];
          this.messageStoreService.upsertMessage({
            type: MessageTypeEnum.error,
            display: this.translateService.instant(translation),
            toast: true,
            sticky: false,
          } as Message);
        }),
      ),
    { dispatch: false },
  );

  completeInventorySuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(INVENTORY_ACTIONS.completeSuccess),
        tap(action => {
          this.inventoryWorksheetService.displayCompleteSuccessModal(
            action.inventory.inventoryId,
          );
        }),
      ),
    { dispatch: false },
  );

  completeInventoryFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(INVENTORY_ACTIONS.completeFail),
        tap(() => {
          this.displayCompleteFailToast();
        }),
      ),
    { dispatch: false },
  );

  createInventoryFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(INVENTORY_ACTIONS.createFail),
        tap(action => {
          this.displayCreateFailToast(!!action.copiedInvName);
        }),
      ),
    { dispatch: false },
  );

  updateQuantityFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(INVENTORY_ITEM_ACTIONS.updateQuantityFail),
        tap(action => {
          if (
            action.error?.message?.includes(
              CompleteInventoryConflictErrorMessage,
            )
          ) {
            this.displayFriendlyFailToast(
              'i18n.inventory.completeInventoryConflictToast',
            );
            this.inventoryService.completeInventoryConflictResolution();
            return;
          }
        }),
      ),
    { dispatch: false },
  );

  //update
  updateInventoryFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          INVENTORY_ACTIONS.updateFail,
          INVENTORY_GROUP_ACTIONS.createFail,
          INVENTORY_ACTIONS.updateWithTracingFail,
        ),
        tap(action => {
          if (
            action.error?.message?.includes(
              CompleteInventoryConflictErrorMessage,
            )
          ) {
            this.displayFriendlyFailToast(
              'i18n.inventory.completeInventoryConflictToast',
            );
            this.inventoryService.completeInventoryConflictResolution();
            return;
          }
          this.displayUpdateFailToast();
        }),
      ),
    { dispatch: false },
  );

  downloadInventoryExtendedCostReportSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(INVENTORY_ACTIONS.downloadExtendedCostReportSuccess),
        tap(action => {
          this.downloadInventoryService.saveExtendedReportBlob(
            action.response,
            convertStringToDate(action.startDate),
            convertStringToDate(action.endDate),
          );
        }),
      ),
    { dispatch: false },
  );

  downloadInventoryExtendedCostReportFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(INVENTORY_ACTIONS.downloadExtendedCostReportFail),
        tap(action => {
          let translation;
          if (action.error.message.includes('Did not find any inventories')) {
            translation =
              'i18n.inventory.downloadExtendedCostReportFailureNotFound';
          } else {
            translation =
              'i18n.inventory.downloadExtendedCostReportFailureGeneral';
          }

          this.displayUpdateFailToast(translation);
        }),
      ),
    { dispatch: false },
  );

  updateWithToastMessageSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(INVENTORY_ACTIONS.updateWithToastMessageSuccess),
        tap(action => {
          if (action.allDuplicates) {
            this.displayUpdateFailToast(
              InventoryToastMessages.copyToGroupDuplicates,
            );
            this.inventoryWorksheetService.resetAllDuplicatesFlag();
            return;
          }

          if (action.productsAdded !== undefined) {
            if (action.productsAdded > 0) {
              if (
                action.messageForToast === 'createNONUSFSuccess' &&
                this.inventoryService.addNonUSFProdFromInventoryWorksheet
              ) {
                this.inventoryService.goToWorksheet(action.inventoryId);
                const translation =
                  InventoryToastMessages[action.messageForToast];
                this.displaySuccessToastNonUSFProductAddFromWorksheet(
                  translation,
                );
              } else if (
                action.messageForToast === 'createNONUSFSuccess' &&
                !this.inventoryService.addNonUSFProdFromInventoryWorksheet
              ) {
                this.displayNonUSFCreateToast(
                  InventoryToastMessages[action.messageForToast],
                  action.inventoryId,
                );
              } else {
                this.inventoryService.goToWorksheet(action.inventoryId);
                const translation =
                  InventoryToastMessages[action.messageForToast];

                this.displaySuccessToast(translation, action.inventoryId, {
                  productsAdded: action.productsAdded.toString(),
                });
              }
            } else {
              if (action.messageForToast.includes('addToGroup')) {
                this.displayUpdateFailToast(
                  InventoryToastMessages['addToGroupFail'],
                );
              }

              if (action.messageForToast.includes('createNONUSF')) {
                this.displayUpdateFailToast(
                  InventoryToastMessages['createNONUSFFail'],
                );
              }
            }
            this.inventoryWorksheetService.resetInventoryAddProductsCount();
            return;
          } else if (action.messageForToast) {
            const translation = InventoryToastMessages[action.messageForToast];
            this.displaySuccessToast(
              translation,
              action.inventoryId,
              action.toastInputParams,
            );
          }
          this.inventoryWorksheetService.resetInventoryItemSelections(
            action.inventoryId,
          );
        }),
      ),
    { dispatch: false },
  );

  updateItemsWithNonUSFDataFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(INVENTORY_ITEM_ACTIONS.nonUSFValueUpdateFail),
        tap(action => {
          this.displayUpdateFailToast(
            'i18n.nonUsfProducts.updateFailureMessage',
          );
        }),
      ),
    { dispatch: false },
  );

  updateWithToastMessageFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(INVENTORY_ACTIONS.updateWithToastMessageFail),
        tap(action => {
          if (
            action.error?.message?.includes(
              CompleteInventoryConflictErrorMessage,
            )
          ) {
            this.displayFriendlyFailToast(
              'i18n.inventory.completeInventoryConflictToast',
            );
            this.inventoryService.completeInventoryConflictResolution();
            return;
          }
          const translation = InventoryToastMessages[action.messageForToast];
          this.displayUpdateFailToast(translation);
        }),
      ),
    { dispatch: false },
  );

  createInventoryGroup$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(INVENTORY_GROUP_ACTIONS.createSuccess),
        tap(() => {
          const scroll = document.getElementById(
            'drag-drop-container',
          ) as HTMLElement;
          let interval;
          let i = 0;
          interval = setInterval(() => {
            scroll.scrollLeft = 0;
            i++;
            if (i === 10) {
              clearInterval(interval);
              const el = document.getElementById(
                'group-name-input-0',
              ) as HTMLIonInputElement;
              el.setFocus();
              setTimeout(() => {
                el.getInputElement().then(input => {
                  input.setSelectionRange(0, input.value.length);
                });
              }, 100);
            }
          }, 10);
          this.inventoryWorksheetService.disableAddGroupButton = false;
        }),
      ),

    { dispatch: false },
  );

  deleteInventorySuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(INVENTORY_ACTIONS.deleteSuccess),
        tap(action => {
          this.messageStoreService.upsertMessage({
            type: MessageTypeEnum.success,
            display: this.translateService.instant(
              'i18n.inventory.deleteInventoryWorksheetModal.successBannerMessage',
              {
                inventoryName: action.inventoryName,
              },
            ),
            toast: true,
            sticky: false,
          } as Message);
        }),
      ),
    { dispatch: false },
  );

  deleteInventoryFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(INVENTORY_ACTIONS.deleteFail),
        tap(() => {
          this.messageStoreService.upsertMessage({
            type: MessageTypeEnum.error,
            display: this.translateService.instant(
              'i18n.inventory.deleteInventoryWorksheetModal.failureBannerMessage',
            ),
            toast: true,
            sticky: false,
          } as Message);
        }),
      ),
    { dispatch: false },
  );

  createGroupFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(INVENTORY_GROUP_ACTIONS.createFail),
        tap(() => {
          this.inventoryWorksheetService.disableAddGroupButton = false;
        }),
      ),
    { dispatch: false },
  );

  online$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(INVENTORY_ACTIONS.exitOffline),
        withLatestFrom(this.inventoryService.multiUserInventoryFlag$),
        tap(([action, multiUserInventoryFlag]) => {
          this.messageStoreService.upsertMessage({
            type: MessageTypeEnum.success,
            display: this.translateService.instant(
              'i18n.inventory.inventoryWorksheet.online',
            ),
            toast: true,
            sticky: false,
          } as Message);
          if (multiUserInventoryFlag) {
            this.store.dispatch(INVENTORY_ITEM_ACTIONS.checkPendingUpdates());
          }
        }),
      ),
    { dispatch: false },
  );

  offline$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(INVENTORY_ACTIONS.enterOffline),
        tap(() => {
          this.messageStoreService.upsertMessage({
            type: MessageTypeEnum.error,
            display: this.translateService.instant(
              'i18n.inventory.inventoryWorksheet.offline',
            ),
            toast: true,
            sticky: false,
          } as Message);
          this.inventoryWorksheetService.updateInventoryWorksheetMode(
            INVENTORY_WORKSHEET_MODE_DEFAULT,
          );
          this.inventoryWorksheetService.closeEverythingForOffline();
        }),
      ),
    { dispatch: false },
  );

  downloadCostReport$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(INVENTORY_ACTIONS.downloadCostReport),
        mergeMap(action => {
          // Load product data
          const productNumbers = this.getProductNumbersFromInventories(
            action.costReportModalOutput.selectedInventories,
          );
          this.productStateService.loadProducts(productNumbers);
          // Make sure product data is loaded before attempting to download
          return combineLatest([
            of(action),
            this.inventoryService.productsForInventoryAreLoaded(productNumbers),
            of(productNumbers),
          ]).pipe(
            filter(([action, isLoaded]) => isLoaded),
            take(1),
          );
        }),
        mergeMap(([action, isLoaded, productNumbers]) => {
          return combineLatest([
            of(action),
            this.inventoryWorksheetService.selectMultipleInventoryWorksheetViewModels$(
              action.costReportModalOutput.selectedInventories,
              true,
            ),
            this.productService.getProductsWithAlternatives(productNumbers),
            this.store.select(
              this.nonUsfProductViewModelSelectors.getAllNonUSFProductsAndConvertToFakeProducts(),
            ),
            this.customerStoreService.selectCustomerDivisionDepartment$(),
          ]).pipe(take(1));
        }),
        tap(
          ([
            action,
            invWorksheetViewModels,
            productsMap,
            nonUSFProducts,
            customerDivisionDepartment,
          ]) => {
            this.downloadInventoryService.downloadInventoryCostReport(
              action.costReportModalOutput,
              invWorksheetViewModels,
              getProductCombinationMapForReports(
                invWorksheetViewModels,
                productsMap,
                nonUSFProducts,
              ),
              customerDivisionDepartment,
            );
          },
        ),
      ),
    { dispatch: false },
  );

  downloadCostComparisonReport$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(INVENTORY_ACTIONS.downloadCostComparisonReport),
        mergeMap(action => {
          // Load product data
          // Have to do error handling after we get product data
          const productNumbers = this.getProductNumbersFromInventories([
            action.costComparisonReportModalOutput.firstSelectedInventory,
            action.costComparisonReportModalOutput.secondSelectedInventory,
          ]);
          this.productStateService.loadProducts(productNumbers);
          // Make sure product data is loaded before attempting to download
          return combineLatest([
            of(action),
            this.inventoryService.productsForInventoryAreLoaded(productNumbers),
            of(productNumbers),
          ]).pipe(
            filter(([action, isLoaded]) => isLoaded),
            take(1),
          );
        }),
        mergeMap(([action, isLoaded, productNumbers]) => {
          return combineLatest([
            of(action),
            this.inventoryWorksheetService.selectMultipleInventoryWorksheetViewModels$(
              [
                action.costComparisonReportModalOutput.firstSelectedInventory,
                action.costComparisonReportModalOutput.secondSelectedInventory,
              ],
              false,
            ),
            this.productService.getProductsWithAlternatives(productNumbers),
            this.store.select(
              this.nonUsfProductViewModelSelectors.getAllNonUSFProductsAndConvertToFakeProducts(),
            ),
            this.customerStoreService.selectCustomerDivisionDepartment$(),
          ]).pipe(take(1));
        }),
        tap(
          ([
            action,
            invWorksheetViewModels,
            productsMap,
            nonUSFProducts,
            customerDivisionDepartment,
          ]) => {
            this.downloadInventoryService.downloadInventoryCostComparisonReport(
              action.costComparisonReportModalOutput,
              invWorksheetViewModels,
              getProductCombinationMapForReports(
                invWorksheetViewModels,
                productsMap,
                nonUSFProducts,
              ),
              customerDivisionDepartment,
            );
          },
        ),
      ),
    { dispatch: false },
  );

  downloadInventory$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(INVENTORY_ACTIONS.downloadInventory),
        mergeMap(action => {
          // Get inventory state to grab product numbers
          return combineLatest([
            of(action),
            this.inventoryService.selectInventoryById$(
              action.downloadInventoryModalOutput.inventoryId,
            ),
          ]).pipe(take(1));
        }),
        mergeMap(([action, inventory]) => {
          // Load product data
          const productNumbers = this.getProductNumbersFromInventories([
            inventory,
          ]);
          this.productStateService.loadProducts(productNumbers);
          // Make sure product data is loaded before attempting to download
          return combineLatest([
            of(action),
            this.inventoryService.productsForInventoryAreLoaded(productNumbers),
            of(productNumbers),
          ]).pipe(
            filter(([action, isLoaded]) => isLoaded),
            take(1),
          );
        }),
        mergeMap(([action, isLoaded, productNumbers]) => {
          return combineLatest([
            of(action),
            this.inventoryWorksheetService.selectInventoryWorksheetViewModel$(
              action.downloadInventoryModalOutput.inventoryId,
            ),
            this.productService.getProductsWithAlternatives(productNumbers),
            this.store.select(
              this.nonUsfProductViewModelSelectors.getAllNonUSFProductsAndConvertToFakeProducts(),
            ),
            this.customerStoreService.selectCustomerDivisionDepartment$(),
          ]).pipe(take(1));
        }),
        tap(
          ([
            action,
            invWorksheetViewModel,
            productsMap,
            nonUSFProducts,
            customerDivisionDepartment,
          ]) => {
            this.downloadInventoryService.downloadInventory(
              action.downloadInventoryModalOutput,
              invWorksheetViewModel,
              getProductCombinationMapForReports(
                [invWorksheetViewModel],
                productsMap,
                nonUSFProducts,
              ),
              customerDivisionDepartment,
            );
          },
        ),
      ),
    { dispatch: false },
  );

  downloadNonUsfProductData$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NonUSFProductActions.downloadNonUsfProductData),
        mergeMap(action => {
          return combineLatest([
            of(action),
            this.store.select(
              this.nonUsfProductViewModelSelectors.getNonUSfProductsByIdForDownload(
                action.nonUsfProductNumbers,
              ),
            ),
          ]).pipe(take(1));
        }),
        tap(([action, nonUsfProducts]) => {
          this.downloadProductsService.downloadNonUsfProducts(nonUsfProducts);
        }),
      ),
    {
      dispatch: false,
    },
  );

  inventoryMigrationToastMessageFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(INVENTORY_ACTIONS.migrationFail),
        tap(action => {
          this.displayInventoryMigrationFailToast();
        }),
      ),
    { dispatch: false },
  );

  inventoryMigrationSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(INVENTORY_ACTIONS.migrationSuccess),
      tap(action => {
        this.displayInventoryMigrationSuccessToast();
      }),
      mergeMap(action => {
        return [ExternalVendorActions.load(), NonUSFProductActions.load()];
      }),
    ),
  );

  resetItemWithLatestProductData$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(INVENTORY_ITEM_ACTIONS.resetItemsWithLatestProductData),
        switchMap(action =>
          combineLatest([
            this.inventoryService.selectInventoryById$(action.inventoryId),
            this.inventoryService.selectInventoryOptionPrice$(),
          ]).pipe(
            take(1),
            switchMap(([inventory, pricingSystemFromState]) => {
              const itemLookup = createInventoryItemLookup(inventory);
              const itemKeys =
                action.pendingItemKeys ||
                this.getUsfItemKeys(inventory.inventoryId, itemLookup);

              return this.handleProductDataReset(
                inventory.inventoryId,
                !!action.priceToggleValue
                  ? action.priceToggleValue
                  : pricingSystemFromState,
                itemKeys,
                itemLookup,
                action.isPriceOverrideReset,
              );
            }),
          ),
        ),
      ),
    { dispatch: false },
  );

  missedItemUpdates$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(INVENTORY_ITEM_ACTIONS.bulkItemUpdateSuccess),
        filter(action => action.missedItems.length > 0),
        withLatestFrom(
          // USF Product Summary
          this.store.select(selectProductSummaryEntities),
          // Non-USF Products
          this.store.select(allNonUSFProducts()),
        ),
        tap(
          ([action, productSummaryEntities, nonUSFProducts]: [
            any,
            any,
            NonUSFProduct[],
          ]) => {
            let translation = '';
            const name = this.getNameForConflictToast(action.lastUpdatedByUser);
            if (action.missedItems.length > 1) {
              translation = this.translateService.instant(
                InventoryToastMessages.missedItemUpdates,
                {
                  name,
                  numberOfProducts: action.missedItems.length,
                },
              );
            } else if (action.missedItems.length === 1) {
              // Check both USF and Non-USF product stores
              const productSummary =
                productSummaryEntities[action.missedItems[0].productNumber];
              const nonUSFProduct = nonUSFProducts.find(
                p => p.productNumber === action.missedItems[0].productNumber,
              );

              const productDescription =
                productSummary?.productDescTxtl ??
                productSummary?.productDescLong ??
                nonUSFProduct?.productDescLong ??
                '';
              translation = this.translateService.instant(
                InventoryToastMessages.missedItemUpdate,
                {
                  name,
                  productDescription: this.titleCase(productDescription),
                },
              );
            }
            this.displayUpdateFailToast(translation);
          },
        ),
      ),
    { dispatch: false },
  );

  private getNameForConflictToast(
    lastUpdatedByUser: InventoryUpdatedByUser,
  ): string {
    if (lastUpdatedByUser.firstName && lastUpdatedByUser.lastName) {
      return `${lastUpdatedByUser.firstName} ${lastUpdatedByUser.lastName}`;
    }
    if (lastUpdatedByUser.firstName) {
      return lastUpdatedByUser.firstName;
    }
    return lastUpdatedByUser.userName;
  }

  private titleCase(str: string): string {
    return str
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  private getUsfItemKeys(
    inventoryId: string,
    itemLookup: Map<string, InventoryItemLookup>,
  ): PendingItemUpdateKey[] {
    const itemKeys: PendingItemUpdateKey[] = [];
    for (const [key, { item, group }] of itemLookup) {
      if (!item.nonUsfFlag) {
        itemKeys.push({
          inventoryId,
          productNumber: item.itemNumber,
          groupName: group.groupName,
        } as PendingItemUpdateKey);
      }
    }
    return itemKeys;
  }

  private handleProductDataReset(
    inventoryId: string,
    pricingSystem: string,
    itemKeys: PendingItemUpdateKey[],
    itemLookup: Map<string, InventoryItemLookup>,
    isPriceOverrideReset: boolean,
  ) {
    if (itemKeys.length === 0) {
      return of();
    }

    const productNumbers = itemKeys.map(key => key.productNumber);

    return this.productService.loadProducts$(productNumbers).pipe(
      switchMap(() =>
        this.store.select(
          this.inventoryCreateSelectors.productsForInventoryById(
            productNumbers,
          ),
        ),
      ),
      filter(productsToFilter =>
        productsAreInStateWithPricing(productsToFilter),
      ),
      tap(productsLoaded => {
        const updates = this.createUpdatesFromProducts(
          itemKeys,
          itemLookup,
          productsLoaded,
          pricingSystem,
          isPriceOverrideReset,
        );
        this.inventoryWorksheetService.dispatchBulkItemUpdate(
          inventoryId,
          updates,
        );
      }),
    );
  }

  private createUpdatesFromProducts(
    itemKeys: PendingItemUpdateKey[],
    itemLookup: Map<string, InventoryItemLookup>,
    productsLoaded: Map<number, Product>,
    pricingSystem: string,
    isPriceOverrideReset: boolean,
  ): InventoryItemUpdateV2[] {
    return itemKeys
      .map(key => {
        const product = productsLoaded.get(key.productNumber);
        if (!product) return null;

        const lookupKey = generatePendingItemUpdateKey(key);
        const lookupResult = itemLookup.get(lookupKey);
        if (!lookupResult) return null;

        const originalItem = { ...lookupResult.item };
        const updatedItem = updateInventoryItemWithProductInformation(
          lookupResult.item,
          product,
          pricingSystem,
        );

        if (isPriceOverrideReset) {
          updatedItem.priceOverrideInd = 'N';
          updatedItem.overrideCasePrice = 0;
        }

        const changedFields = this.getChangedItemFields(
          originalItem,
          updatedItem,
        );

        if (!changedFields) return null;

        return {
          groupName: lookupResult.group.groupName,
          itemNumber: key.productNumber,
          itemData: changedFields,
        };
      })
      .filter(update => update !== null);
  }

  private getChangedItemFields(
    originalItem: InventoryGroupItem,
    updatedItem: InventoryGroupItem,
  ): Partial<InventoryGroupItem> {
    const fieldsToCompare = [
      'productPackSizeLabel',
      'productUnitPrice',
      'productCasePrice',
      'productUnitOfMeasure',
      'productBreakable',
      'productConvFactor',
      'productCatchWeightFlag',
      'priceOverrideInd',
      'overrideCasePrice',
    ];

    const changedFields = {};

    fieldsToCompare.forEach(field => {
      // Special handling for priceOverrideInd and overrideCasePrice when isPriceOverrideReset is true
      if (
        field === 'priceOverrideInd' &&
        updatedItem[field] === 'N' &&
        originalItem[field] !== 'N'
      ) {
        changedFields[field] = 'N';
      } else if (
        field === 'overrideCasePrice' &&
        updatedItem[field] === 0 &&
        originalItem[field] !== 0
      ) {
        changedFields[field] = 0;
      } else if (originalItem[field] !== updatedItem[field]) {
        changedFields[field] = updatedItem[field];
      }
    });

    return Object.keys(changedFields).length > 0 ? changedFields : null;
  }

  getProductNumbersFromInventories(inventories: InventoryState[]): number[] {
    const productNumbersArr = [];
    const productNumbersMap = new Set();
    for (const inventory of inventories) {
      if (inventory.groups) {
        for (const group of inventory.groups) {
          if (group.groupItems) {
            for (const item of group.groupItems) {
              if (!productNumbersMap.has(item.itemNumber) && !item.nonUsfFlag) {
                productNumbersArr.push(item.itemNumber);
                productNumbersMap.add(item.itemNumber);
              }
            }
          }
        }
      }
    }
    return productNumbersArr;
  }

  displaySuccessToast(
    successMessage: string,
    inventoryId: string,
    toastInputParams?: ToastParams,
    isDuplicate?: boolean,
  ): void {
    const message = isDuplicate
      ? successMessage
      : this.translateService.instant(successMessage, toastInputParams);
    this.toastService.presentToastMsg(
      message,
      'green-toast',
      MessageTypeEnum.success,
      [],
    );
  }

  displaySuccessToastNonUSFProductAddFromWorksheet(
    successMessage: string,
  ): void {
    this.toastService.presentToastMsg(
      this.translateService.instant(successMessage),
      'green-toast',
      MessageTypeEnum.success,
      [],
    );
  }

  displayNonUSFCreateToast(successMessage: string, inventoryId: string) {
    let buttons = [];
    buttons.push({
      text: this.translateService.instant('i18n.inventory.goToWorksheet2'),
      side: 'end',
      handler: () => {
        this.inventoryService.goToWorksheet(inventoryId);
      },
    });
    this.toastService.presentToastMsg(
      this.translateService.instant(successMessage),
      'green-toast',
      MessageTypeEnum.success,
      buttons,
    );
  }

  async displayCompleteFailToast() {
    const toast = await this.toastController.create({
      message: this.translateService.instant(
        'i18n.inventory.inventoryWorksheet.completeInventoryFail',
      ),
      cssClass: 'red-toast',
      duration: null,
      buttons: [
        {
          icon: 'close-circle',
          role: 'cancel',
        },
      ],
      position: 'top',
      icon: this.toastService.setToastIcon(MessageTypeEnum.error),
    } as ToastOptions);
    await toast.present();
    await toast.onDidDismiss();
    this.inventoryWorksheetService.disableCompleteButton = false;
  }

  displayUpdateFailToast(errorMessage?: string): void {
    const errorText = errorMessage
      ? errorMessage
      : 'i18n.inventory.inventoryWorksheet.updateInventoryFail';
    this.messageStoreService.upsertMessage({
      type: MessageTypeEnum.error,
      display: this.translateService.instant(errorText),
      toast: true,
      sticky: true,
    } as Message);
  }

  displayFriendlyFailToast(errorMessage: string): void {
    this.messageStoreService.upsertMessage({
      type: MessageTypeEnum.success,
      display: this.translateService.instant(errorMessage),
      toast: true,
      sticky: true,
    } as Message);
  }

  displayCreateFailToast(isDuplicate: boolean): void {
    this.messageStoreService.upsertMessage({
      type: MessageTypeEnum.error,
      display: isDuplicate
        ? this.translateService.instant(
            'i18n.inventory.createCopiedInventoryFailure',
          )
        : this.translateService.instant(
            'i18n.inventory.createInventoryFailure',
          ),
      toast: true,
      sticky: false,
    } as Message);
  }

  displayCreateWithMissingProductsToast(failedProducts: number): void {
    this.messageStoreService.upsertMessage({
      type: MessageTypeEnum.error,
      display: this.translateService.instant(
        'i18n.inventory.createInventorySuccessWithMissingProducts',
        { failedProducts: failedProducts },
      ),
      toast: true,
      sticky: true,
    } as Message);
  }

  displayInventoryMigrationSuccessToast(): void {
    const display = this.translateService.instant(
      'i18n.inventory.inventoryMigration.yourInventoryDataSuccess',
    );
    let buttons = [];
    buttons.push({
      text: this.translateService.instant(
        'i18n.inventory.inventoryMigration.goToYourInventories',
      ),
      side: 'end',
      handler: () => {
        this.inventoryService.goTo([PATHS.INVENTORY, PATHS.ALL_INVENTORY]);
      },
    });
    this.toastService.presentToastMsg(
      display,
      'green-toast',
      MessageTypeEnum.success,
      buttons,
    );
  }

  displayInventoryMigrationFailToast(): void {
    const errorText = this.translateService.instant(
      'i18n.inventory.inventoryMigration.migrationFailToastMsg',
    );
    this.messageStoreService.upsertMessage({
      type: MessageTypeEnum.error,
      display: this.translateService.instant(errorText),
      toast: true,
      sticky: true,
    } as Message);
  }

  constructor(
    private actions$: Actions,
    private toastService: ToastService,
    private translateService: TranslateService,
    private inventoryWorksheetService: InventoryWorksheetService,
    private inventoryService: InventoryService,
    private messageStoreService: MessageStoreService,
    private toastController: ToastController,
    private productStateService: ProductStateService,
    private productService: ProductService,
    private downloadInventoryService: DownloadInventoryService,
    private downloadProductsService: DownloadProductsService,
    private customerStoreService: CustomerStoreService,
    public store: Store,
    public nonUsfProductViewModelSelectors: NonUsfProductViewModelSelectors,
    private inventoryCreateSelectors: InventoryCreateSelectors,
  ) {}
}
